import { privacyPolicyMarkdownMock } from '@/mocks/privacyPolicyMarkdown';
import { termsOfUseMarkdownMock } from '@/mocks/termsOfUseMarkdown';
import { api, TCustomAxiosPaginationResponse } from '..';
import { TLegalDocument, TLegalDocumentType } from '@/types/Legal.types';
import { apiPaths } from '@/services/api/paths';
import { LEGAL_DOCUMENT_TYPE_LABELS } from '@/constants/stringVars';
import { SUPER_ADMIN_PAGE_SIZE } from '@/constants/numberVars';

export type TLegalDocumentListFilters = {
  type?: TLegalDocumentType;
  status?: 'active' | 'inactive';
};

export const getAllLegalDocuments = async (
  page: number,
  filterParams?: TLegalDocumentListFilters,
): Promise<TCustomAxiosPaginationResponse<TLegalDocument[]>> => {
  const params: { page: number; limit: number; type?: string; status?: string } = {
    page,
    limit: SUPER_ADMIN_PAGE_SIZE,
  };
  if (filterParams?.type) params.type = filterParams.type;
  if (filterParams?.status) params.status = filterParams.status;

  const { data } = await api.get(apiPaths.legal.root, { params });

  return data;
};

export const getLegalDocumentsByType = async (type: string): Promise<TLegalDocument[]> => {
  const { data } = await api.get(apiPaths.legal.byType(type));

  return Array.isArray(data) ? data : data.data;
};

export const postCreateLegalDocument = async ({ type }: { type: string }): Promise<TLegalDocument> => {
  const formattedType = type.toLowerCase().replaceAll('_', '-');

  const templateContent = formattedType === 'privacy-policy' ? privacyPolicyMarkdownMock : termsOfUseMarkdownMock;

  // Use the friendly label for the H1 (e.g. "Content Policy") rather than the raw
  // enum value ("CONTENT_POLICY"); fall back to a humanized type for any value
  // without a label.
  const documentTitle = LEGAL_DOCUMENT_TYPE_LABELS[type as TLegalDocumentType] ?? type.replaceAll('_', ' ');

  const emptyMarkdownContent = `# ${documentTitle} \n\n${templateContent}`;

  const { data } = await api.post(apiPaths.legal.root, {
    type,
    content: emptyMarkdownContent,
    isActive: false,
  });

  const newDocument = data.data;

  return newDocument;
};

export const getLegalDocumentById = async (id: string): Promise<TLegalDocument> => {
  const { data } = await api.get(apiPaths.legal.byId(id));

  const document = data.data;

  return document;
};

export const putUpdateLegalDocument = async ({
  id,
  content,
  type,
  version,
  s3Key,
  isActive,
}: {
  id: string;
  content: string;
  type: string;
  version: string;
  s3Key: string;
  isActive: boolean;
}): Promise<TLegalDocument> => {
  const { data } = await api.put(apiPaths.legal.byId(id), {
    content,
    type,
    version,
    s3Key,
    isActive,
  });

  const updatedDocument = data.data;

  return updatedDocument;
};

export const putActivateLegalDocument = async ({
  type,
  version,
}: {
  type: string;
  version: string;
}): Promise<TLegalDocument> => {
  const { data } = await api.put(apiPaths.legal.activate(type, version));

  const updatedDocument = data.data;

  return updatedDocument;
};

export const putDeactivateLegalDocument = async ({
  type,
  version,
}: {
  type: string;
  version: string;
}): Promise<TLegalDocument> => {
  const { data } = await api.put(apiPaths.legal.deactivate(type, version));

  const updatedDocument = data.data;

  return updatedDocument;
};
